/**
 * Security utilities for the K-12 Admin Assistant Platform
 */

import DOMPurify from 'dompurify';

/**
 * Input sanitization utilities
 */
export const sanitizeInput = {
  /**
   * Sanitize HTML content
   */
  html: (input: string): string => {
    return DOMPurify.sanitize(input, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li'],
      ALLOWED_ATTR: [],
    });
  },

  /**
   * Sanitize text content (remove all HTML)
   */
  text: (input: string): string => {
    return DOMPurify.sanitize(input, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  },

  /**
   * Sanitize email address
   */
  email: (input: string): string => {
    // Remove whitespace and convert to lowercase
    const cleaned = input.trim().toLowerCase();

    // Basic email validation pattern
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(cleaned)) {
      throw new Error('Invalid email format');
    }

    return cleaned;
  },

  /**
   * Sanitize phone number
   */
  phone: (input: string): string => {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, '');

    // Validate US phone number format
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length === 11 && digits[0] === '1') {
      return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    throw new Error('Invalid phone number format');
  },

  /**
   * Sanitize URL
   */
  url: (input: string): string => {
    try {
      const url = new URL(input);

      // Only allow http and https protocols
      if (!['http:', 'https:'].includes(url.protocol)) {
        throw new Error('Invalid URL protocol');
      }

      return url.toString();
    } catch {
      throw new Error('Invalid URL format');
    }
  },
};

/**
 * Content Security Policy utilities
 */
export const cspUtils = {
  /**
   * Generate CSP header value
   */
  generateCSP: (): string => {
    const directives = [
      "default-src 'self'",
      "script-src 'self' 'unsafe-inline'", // Note: 'unsafe-inline' should be avoided in production
      "style-src 'self' 'unsafe-inline' https://fonts.googleapis.com",
      "font-src 'self' https://fonts.gstatic.com",
      "img-src 'self' data: https:",
      "connect-src 'self' http://localhost:8000 https://api.example.com",
      "frame-ancestors 'none'",
      "base-uri 'self'",
      "form-action 'self'",
    ];

    return directives.join('; ');
  },

  /**
   * Set CSP meta tag
   */
  setCSPMeta: (): void => {
    const metaTag = document.createElement('meta');
    metaTag.httpEquiv = 'Content-Security-Policy';
    metaTag.content = cspUtils.generateCSP();
    document.head.appendChild(metaTag);
  },
};

/**
 * File validation utilities
 */
export const fileValidation = {
  /**
   * Validate file type
   */
  validateFileType: (fileName: string, allowedTypes: string[]): boolean => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    return extension ? allowedTypes.includes(extension) : false;
  },

  /**
   * Validate file size
   */
  validateFileSize: (file: File, maxSizeInMB: number): boolean => {
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    return file.size <= maxSizeInBytes;
  },

  /**
   * Validate image file
   */
  validateImage: (file: File): Promise<boolean> => {
    return new Promise(resolve => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = URL.createObjectURL(file);
    });
  },
};

/**
 * URL validation utilities
 */
export const urlValidation = {
  /**
   * Validate URL against allowed domains
   */
  validateDomain: (url: string, allowedDomains: string[]): boolean => {
    try {
      const urlObj = new URL(url);
      return allowedDomains.some(domain => urlObj.hostname.endsWith(domain));
    } catch {
      return false;
    }
  },

  /**
   * Check if URL is safe (not malicious)
   */
  isSafeURL: (url: string): boolean => {
    const dangerousPatterns = [
      /javascript:/i,
      /data:/i,
      /vbscript:/i,
      /file:/i,
      /ftp:/i,
    ];

    return !dangerousPatterns.some(pattern => pattern.test(url));
  },
};

/**
 * Rate limiting utilities (client-side awareness)
 */
export class RateLimiter {
  private requests: number[] = [];
  private windowMs: number;
  private maxRequests: number;

  constructor(windowMs = 60000, maxRequests = 100) {
    this.windowMs = windowMs;
    this.maxRequests = maxRequests;
  }

  /**
   * Check if request is allowed
   */
  isAllowed(): boolean {
    const now = Date.now();

    // Remove old requests outside the window
    this.requests = this.requests.filter(time => now - time < this.windowMs);

    // Check if we're under the limit
    if (this.requests.length < this.maxRequests) {
      this.requests.push(now);
      return true;
    }

    return false;
  }

  /**
   * Get remaining requests
   */
  getRemainingRequests(): number {
    const now = Date.now();
    this.requests = this.requests.filter(time => now - time < this.windowMs);
    return Math.max(0, this.maxRequests - this.requests.length);
  }
}

/**
 * Token management utilities
 */
export const tokenUtils = {
  /**
   * Validate JWT token format (basic check)
   */
  isValidJWTFormat: (token: string): boolean => {
    const parts = token.split('.');
    return parts.length === 3 && parts.every(part => part.length > 0);
  },

  /**
   * Check if token is expired (basic check)
   */
  isTokenExpired: (token: string): boolean => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3 || !parts[1]) {
        return true;
      }
      const payload = JSON.parse(atob(parts[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp < now;
    } catch {
      return true; // Invalid token format
    }
  },

  /**
   * Get token expiration time
   */
  getTokenExpiration: (token: string): Date | null => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3 || !parts[1]) {
        return null;
      }
      const payload = JSON.parse(atob(parts[1]));
      return new Date(payload.exp * 1000);
    } catch {
      return null;
    }
  },
};

/**
 * Secure storage utilities
 */
export const secureStorage = {
  /**
   * Store sensitive data securely
   */
  setItem: (key: string, value: string): void => {
    try {
      // In a real application, you might want to encrypt the value
      sessionStorage.setItem(key, value);
    } catch (error) {
      console.error('Failed to store item securely:', error);
    }
  },

  /**
   * Retrieve sensitive data securely
   */
  getItem: (key: string): string | null => {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      console.error('Failed to retrieve item securely:', error);
      return null;
    }
  },

  /**
   * Remove sensitive data
   */
  removeItem: (key: string): void => {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to remove item securely:', error);
    }
  },

  /**
   * Clear all sensitive data
   */
  clear: (): void => {
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error('Failed to clear secure storage:', error);
    }
  },
};

/**
 * Input validation utilities
 */
export const inputValidation = {
  /**
   * Validate password strength
   */
  validatePassword: (
    password: string
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }

    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }

    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }

    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  /**
   * Validate email format
   */
  validateEmail: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  /**
   * Validate required fields
   */
  validateRequired: (
    fields: Record<string, unknown>
  ): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    Object.entries(fields).forEach(([key, value]) => {
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        errors.push(`${key} is required`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  },
};

/**
 * XSS protection utilities
 */
export const xssProtection = {
  /**
   * Escape HTML characters
   */
  escapeHtml: (text: string): string => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  },

  /**
   * Sanitize user input for display
   */
  sanitizeForDisplay: (input: string): string => {
    return DOMPurify.sanitize(input, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    });
  },

  /**
   * Validate and sanitize HTML content
   */
  sanitizeHTML: (html: string): string => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'b',
        'i',
        'em',
        'strong',
        'p',
        'br',
        'ul',
        'ol',
        'li',
        'a',
      ],
      ALLOWED_ATTR: ['href', 'title'],
      ALLOWED_URI_REGEXP: /^https?:\/\//,
    });
  },
};

/**
 * Error handling utilities
 */
export const errorHandling = {
  /**
   * Sanitize error messages for display
   */
  sanitizeErrorMessage: (error: unknown): string => {
    if (typeof error === 'string') {
      return xssProtection.sanitizeForDisplay(error);
    }

    if (error instanceof Error) {
      return xssProtection.sanitizeForDisplay(error.message);
    }

    return 'An unexpected error occurred';
  },

  /**
   * Log security-related errors
   */
  logSecurityError: (
    error: string,
    context?: Record<string, unknown>
  ): void => {
    console.error('Security Error:', error, context);

    // In a real application, you might want to send this to a logging service
    // logToService({ type: 'security', error, context, timestamp: new Date() });
  },
};
